/**
 * The image rules of each social platform: the file type, the file size and the shape that a
 * platform takes for an image post. The Composer (music24-app) shows the refusal next to the
 * channel, and music24-api refuses the schedule with the same rule (`assertPostMediaFits`), so an
 * image never fails at the hour of its post. The file has no imports, so every project can use it.
 *
 * Instagram (Graph API, content publishing): JPEG only, at most 8 MB, a feed image from 4:5 (0.8)
 * to 1.91:1. A story takes a 9:16 image (and the other shapes, with bars), so the feed shape rule
 * does not apply to a story. TikTok photo posts (Content Posting API): JPEG or WebP, not PNG.
 */

export type PostImagePlatform = 'tiktok' | 'instagram' | 'youtube' | 'facebook' | 'threads' | 'x';

export type PostImageFormat = 'post' | 'story';

/** The largest image that Instagram takes. The web upload makes its JPEG at most this large. */
export const INSTAGRAM_IMAGE_MAX_BYTES = 8 * 1024 * 1024;

/** The largest image that X takes. */
export const X_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

/** The narrowest Instagram feed image: 4:5. */
export const INSTAGRAM_FEED_MIN_RATIO = 4 / 5;

/** The widest Instagram feed image: 1.91:1. */
export const INSTAGRAM_FEED_MAX_RATIO = 1.91;

/** A small margin, so a 1080 × 1350 (0.8) or 1200 × 628 (1.911) image passes. */
const RATIO_MARGIN = 0.01;

export interface PostImage {
  /** The MIME type; null when not known (the rule then checks no type). */
  mimeType: string | null;
  /** Pixels; null when not known (the rule then checks no shape). */
  width: number | null;
  height: number | null;
  /** Bytes; null when not known (the rule then checks no size). */
  sizeBytes: number | null;
}

export type PostImageRefusal =
  | { reason: 'aspect'; ratio: number; minRatio: number; maxRatio: number }
  | { reason: 'type'; mimeType: string; allowed: ReadonlyArray<string> }
  | { reason: 'size'; sizeBytes: number; maxBytes: number };

interface PlatformImageRule {
  types?: ReadonlyArray<string>;
  maxBytes?: number;
  /** The shape of a feed image; a story has no shape rule. */
  feedRatio?: { min: number; max: number };
}

const RULES: Partial<Record<PostImagePlatform, PlatformImageRule>> = {
  instagram: {
    types: ['image/jpeg'],
    maxBytes: INSTAGRAM_IMAGE_MAX_BYTES,
    feedRatio: { min: INSTAGRAM_FEED_MIN_RATIO, max: INSTAGRAM_FEED_MAX_RATIO },
  },
  tiktok: { types: ['image/jpeg', 'image/webp'] },
  /** X takes JPEG, PNG, WebP and GIF images of at most 5 MB (`X_IMAGE_MAX_BYTES`). */
  x: { types: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'], maxBytes: X_IMAGE_MAX_BYTES },
};

/** The JPEG MIME type has an old twin that some systems send. */
function normalType(mimeType: string): string {
  const type = mimeType.toLowerCase().split(';')[0]?.trim() ?? '';
  return type === 'image/jpg' || type === 'image/pjpeg' ? 'image/jpeg' : type;
}

/**
 * Why `platform` refuses this image in this format, or null when it takes it (or when nothing is
 * known that it could refuse). The first broken rule wins: type, then size, then shape.
 */
export function postImageRefusal(platform: string, format: PostImageFormat, image: PostImage): PostImageRefusal | null {
  const rule = RULES[platform as PostImagePlatform];
  if (rule === undefined) {
    return null;
  }
  if (rule.types !== undefined && image.mimeType !== null && !rule.types.includes(normalType(image.mimeType))) {
    return { reason: 'type', mimeType: image.mimeType, allowed: rule.types };
  }
  if (rule.maxBytes !== undefined && image.sizeBytes !== null && image.sizeBytes > rule.maxBytes) {
    return { reason: 'size', sizeBytes: image.sizeBytes, maxBytes: rule.maxBytes };
  }
  if (
    format === 'post' &&
    rule.feedRatio !== undefined &&
    image.width !== null &&
    image.height !== null &&
    image.width > 0 &&
    image.height > 0
  ) {
    const ratio = image.width / image.height;
    if (ratio < rule.feedRatio.min - RATIO_MARGIN || ratio > rule.feedRatio.max + RATIO_MARGIN) {
      return { reason: 'aspect', ratio, minRatio: rule.feedRatio.min, maxRatio: rule.feedRatio.max };
    }
  }
  return null;
}

/** The common names of the shapes, for the message ("This one is 3:1."). */
const NAMED_RATIOS: ReadonlyArray<{ label: string; ratio: number }> = [
  { label: '9:16', ratio: 9 / 16 },
  { label: '2:3', ratio: 2 / 3 },
  { label: '3:4', ratio: 3 / 4 },
  { label: '4:5', ratio: 4 / 5 },
  { label: '1:1', ratio: 1 },
  { label: '4:3', ratio: 4 / 3 },
  { label: '3:2', ratio: 3 / 2 },
  { label: '16:9', ratio: 16 / 9 },
  { label: '1.91:1', ratio: 1.91 },
  { label: '2:1', ratio: 2 },
  { label: '3:1', ratio: 3 },
];

/** "3:1", "9:16", else "2.4:1" (two decimals at most). */
export function ratioLabel(ratio: number): string {
  const named = NAMED_RATIOS.find((one) => Math.abs(one.ratio - ratio) / one.ratio < 0.02);
  if (named !== undefined) {
    return named.label;
  }
  return `${Number(ratio.toFixed(2))}:1`;
}
